/**
 * Unified Loading System - Full Loader Variant
 *
 * Full-screen loading overlay with rotating tips.
 * Used for long-running operations like CV generation.
 *
 * @example
 * ```tsx
 * <FullLoader
 *   message="Generating your CV"
 *   submessage="This may take up to 2 minutes"
 *   progress={45}
 *   tips={tips}
 *   showBackgroundTaskNotice={true}
 * />
 * ```
 */

import { useEffect, useState, useCallback } from 'react';
import { Loader2, Info } from 'lucide-react';
import type { FullLoaderProps, Tip } from '../types';

/**
 * Utility for merging class names
 */
function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Default tips if none provided
 */
const DEFAULT_TIPS: Tip[] = [
  {
    title: 'Please wait',
    text: 'This operation may take a minute or two. Your progress is being saved.',
  },
];

/**
 * Tip rotation interval in milliseconds
 */
const TIP_ROTATION_INTERVAL = 5000;

/**
 * Fade animation duration in milliseconds
 */
const FADE_DURATION = 500;

/**
 * Single Tip Display Component
 */
interface TipDisplayProps {
  tip: Tip;
  isFadingOut: boolean;
  isFadingIn: boolean;
}

function TipDisplay({ tip, isFadingOut, isFadingIn }: TipDisplayProps) {
  return (
    <div
      className={cn(
        'tip-display',
        'flex items-start gap-3 p-4 rounded-lg',
        'bg-indigo-50 dark:bg-indigo-950/30',
        'border border-indigo-100 dark:border-indigo-900/50',
        'transition-opacity duration-500 ease-in-out',
        isFadingOut && 'opacity-0',
        isFadingIn && 'opacity-100'
      )}
      role="status"
      aria-live="polite"
    >
      {/* Info icon */}
      <Info
        size={20}
        className="text-indigo-500 dark:text-indigo-400 flex-shrink-0 mt-0.5"
        aria-hidden="true"
      />

      {/* Tip content */}
      <div className="flex-1">
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">
          {tip.title}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
          {tip.text}
        </p>
      </div>
    </div>
  );
}

/**
 * FullLoader - Full-screen loading overlay with rotating tips
 *
 * Displays a centered loading state with:
 * - Main message and submessage
 * - Optional progress bar
 * - Rotating tips that change every 5 seconds
 * - Optional background task notice
 */
export function FullLoader({
  message,
  submessage,
  progress,
  tips = DEFAULT_TIPS,
  showBackgroundTaskNotice = true,
  fullScreen = false,
  className,
  ariaLabel,
}: FullLoaderProps) {
  // Use only the first tip if tips are provided
  // (Rotation could be added later as an enhancement)
  const activeTip = tips && tips.length > 0 ? tips[0] : DEFAULT_TIPS[0];

  // Generate accessible label
  const accessibilityLabel = ariaLabel || message || 'Loading';

  // Container classes based on fullScreen prop
  const containerClasses = cn(
    'full-loader',
    fullScreen
      ? 'fixed inset-0 z-50 flex items-center justify-center bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm'
      : 'flex flex-col items-center justify-center p-6',
    className
  );

  return (
    <div
      className={containerClasses}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={accessibilityLabel}
    >
      {/* Content wrapper - limits max width for readability */}
      <div className="w-full max-w-md">
        {/* Spinner */}
        <div className="flex justify-center mb-6">
          <div
            className={cn(
              'flex items-center justify-center rounded-full',
              'bg-indigo-500 dark:bg-indigo-600',
              'shadow-lg shadow-indigo-500/30 dark:shadow-indigo-600/30'
            )}
            style={{ width: 64, height: 64 }}
            aria-hidden="true"
          >
            <Loader2
              size={32}
              className="text-white animate-spin"
              strokeWidth={3}
            />
          </div>
        </div>

        {/* Main message */}
        {message && (
          <div className="text-center mb-2">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {message}
            </h2>
          </div>
        )}

        {/* Submessage */}
        {submessage && (
          <div className="text-center mb-6">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {submessage}
            </p>
          </div>
        )}

        {/* Progress bar (if progress provided) */}
        {progress !== undefined && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                Progress
              </span>
              <span className="text-xs font-medium text-gray-900 dark:text-gray-100">
                {Math.round(Math.min(progress, 100))}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div
                className="h-full bg-indigo-500 dark:bg-indigo-600 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${Math.min(progress, 100)}%` }}
                role="progressbar"
                aria-valuenow={Math.round(Math.min(progress, 100))}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Loading progress"
              />
            </div>
          </div>
        )}

        {/* Tip display */}
        {activeTip && (
          <div className="mb-6">
            <TipDisplay
              tip={activeTip}
              isFadingOut={false}
              isFadingIn={true}
            />
          </div>
        )}

        {/* Background task notice */}
        {showBackgroundTaskNotice && (
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <Info size={14} aria-hidden="true" />
            <span>This operation runs in the background. You can safely wait.</span>
          </div>
        )}

        {/* Screen reader only progress announcement */}
        <div className="sr-only" aria-live="polite">
          {message}. {progress !== undefined ? `${Math.round(progress)}% complete.` : ''}
        </div>
      </div>
    </div>
  );
}

/**
 * FullLoaderWithTipRotation - Full loader with automatic tip rotation
 *
 * Enhanced version that rotates through tips every TIP_ROTATION_INTERVAL ms.
 * Tips fade out and fade in smoothly.
 */
export function FullLoaderWithTipRotation({
  tips = DEFAULT_TIPS,
  ...props
}: FullLoaderProps) {
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const [, setIsTransitioning] = useState(false);

  const rotateToNextTip = useCallback(() => {
    if (tips.length <= 1) return;

    setIsTransitioning(true);

    // Wait for fade out, then switch tip and fade in
    setTimeout(() => {
      setCurrentTipIndex((prev) => (prev + 1) % tips.length);
      setTimeout(() => {
        setIsTransitioning(false);
      }, FADE_DURATION);
    }, FADE_DURATION);
  }, [tips.length]);

  // Set up tip rotation interval
  useEffect(() => {
    if (tips.length <= 1) return;

    const interval = setInterval(() => {
      rotateToNextTip();
    }, TIP_ROTATION_INTERVAL);

    return () => clearInterval(interval);
  }, [rotateToNextTip, tips.length]);

  // Override the active tip with current index
  const currentTip = tips[currentTipIndex] || DEFAULT_TIPS[0];

  return (
    <FullLoader
      {...props}
      tips={[currentTip]}
    />
  );
}

export default FullLoader;
